// © Microsoft Corporation. All rights reserved.

import { CanonicalNamePathMap, PeregrineRoute, RouteNames, getRoutePath, peregrineRouter } from "@msnews/peregrine-router";
import { CommonHeaderConfig, NavigationItem, RenderType, SecondaryNavigationInfo } from "./CommonHeader.config";
import { CommonHeaderConnectorArgs, CommonHeaderInspector } from "./CommonHeader.inspector";
import { CommonHeaderInvalidUrl, CommonHeaderMissingFormCode, CommonHeaderNavigationFailed, waitForTtvr } from "@msnews/diagnostics";
import { CommonHeaderProps, CommonHeaderTelemetry, CommonHeaderTelemetryContext, ContextualNavItem, HamburgerMenuItem, SearchTelemetryContext } from "./CommonHeader.props";
import { ConnectorNamespaces, compareDisplayNameDesc, windowsMarkets } from "@msnews/experiences-constants";
import { IRequestContext, InspectorArgs, InspectorResponse, PageUtility, UrlUtility, appEnvironment, getAppEnvironment, getContextData, headData } from "@msnews/core";
import { Telemetry, TelemetryActionType, TelemetryBehaviorType, TelemetryContentType, TelemetryMetadataAttribute, TelemetryObjectBase, TelemetryType } from "@msnews/telemetry-contracts";
import { cloneDeep, head } from "lodash-es";

import { CommonHeaderActions } from "./CommonHeader.actions";
import { CommonHeaderState } from "./CommonHeader.state";
import { ComponentConnector } from "@msnews/experiences-connector";
import { DynamicReducer } from "@msnews/experiences-dynamic-reducer";
import { ExperienceInitialProps } from "@msnews/experiences-base";
import { ExperiencesSharedStateState } from "@msnews/experiences-shared-state";
import { IReducer } from "@msnews/experiences-redux-contracts";
import { PageBase } from "@msnews/experiences-page-base";
import { SearchFormParameters } from "@msnews/search-box";
import { Store } from "react-redux";
import { TelemetryDOM } from "@msnews/telemetry-core";
import { VerticalPropertyDataConnector } from "@msnews/vertical-property-data-connector";
import { WindowsPageSettingsActions } from "@msnews/windows-page-settings";
import { isNullOrWhiteSpace } from "@microsoft/fast-web-utilities";
import { pdpServiceClient } from "@msnews/pdp-service-library";
import { isWindowsFlightEnabled, WindowsFlights } from "@msnews/windows-shared";
import { defineVerticalHeaderFont } from "@msnews/msn-common-header";

/**
 * List of query params to clean from URL when navigating. Created to resolve https://dev.azure.com/msasg/ContentServices/_workitems/edit/3601974
 * so that extra query params from weather pages don't persist on other pages.
 *
 * Created the following task for proper discussion on how to handle query params: https://dev.azure.com/msasg/ContentServices/_workitems/edit/3616137
 */
const blockedQueryParams = ["weadegreetype", "zoom", "lat", "lon", "iso", "weaext0", "el", "city", "type", "animation", "form", "catid", "pid", "loc", "scenario"];

/** Form code name */
const FormCodeInputName = "form";

/**
 * The connector for the CommonHeader Experience.
 */
export class CommonHeaderConnector extends ComponentConnector<CommonHeaderConfig, CommonHeaderState, CommonHeaderProps, any> {
    /**
     * Use Telemetry.sendActionEvent when available (Peregrine). Otherwise, set custom event tracking function with setTrackingFunctions (Prime).
     */
    private sendActionEvent: (element?, action?, behavior?, event?, destination?, headline?) => void;

    /**
     * Use Telemetry.sendAppErrorEvent when available (Peregrine). Otherwise, set custom event tracking function with setTrackingFunctions (Prime).
     */
    private sendAppErrorEvent: (appError) => void;

    /** The vertical property data connector, if present (i.e. on a vertical page) */
    private verticalPropertyDataConnector: VerticalPropertyDataConnector;

    /** The vertical key, if it exists for the page */
    private verticalKey: string;

    /** Prime vertical key used to override the originally selected pivot on component connect when primePathOverride cannot be matched */
    private verticalKeyOverride: string;

    /** Root url to construct full urls with nav item paths */
    private overrideRootUrl: string;

    /** Contextual navigation telemetry object */
    private contextualNavTelemetry: TelemetryObjectBase;

    /** Hamburger menu telemetry object */
    private hamburgerMenuTelemetry: TelemetryObjectBase;

    /** Common header telemetry context */
    private commonHeaderTelemetryContext: CommonHeaderTelemetryContext = {};

    /** Telemetry data for the search box */
    private searchTelemetryContext: SearchTelemetryContext;

    /** Flag to determine fi the secondary nav is enabled */
    private isContextualSecondaryNavEnabled: boolean;

    /** Object with necessary information for rendering 2 level of navigation */
    private primeSecondaryNavigationInfo: SecondaryNavigationInfo;

    private isVerticalBrandingEnabled = isWindowsFlightEnabled(WindowsFlights.VerticalBranding1);

    /**
     * Url segment from Prime including /{vertical}/{category}/{subcategory}. This will override the originally selected pivot if the path
     * can be matched to a route.
     */
    private primePathOverride: string;

    public setOverrideRootUrl(overrideRootUrl: string) {
        this.overrideRootUrl = overrideRootUrl;
    }

    public setIsContextualSecondaryNavEnabled(isContextualSecondaryNavEnabled: boolean) {
        this.isContextualSecondaryNavEnabled = isContextualSecondaryNavEnabled;
    }

    public setPrimeSecondaryNavigationInfo(secondaryNavInfo: SecondaryNavigationInfo) {
        this.primeSecondaryNavigationInfo = secondaryNavInfo;
    }

    public setVerticalKeyOverride(verticalKeyOverride: string) {
        this.verticalKeyOverride = verticalKeyOverride;
    }

    public setPrimePathOverride(primePathOverride: string) {
        this.primePathOverride = primePathOverride;
    }

    /**
     * Initializes the Common Header connector
     * @param namespace Redux subspace name for the connector
     * @param parentNamespace Redux subspace name for the parent connector
     * @param rootReducer Application root reducer
     * @param reducer The connector reducer
     * @param store The store or redux subspace that the connector will be connected to
     * @param config The common header config
     * @param requestContext The request context
     */
    public constructor(
        namespace: string,
        parentNamespace: string,
        rootReducer: DynamicReducer,
        reducer: IReducer<CommonHeaderState>,
        store: Store<any>,
        config: CommonHeaderConfig,
        requestContext: IRequestContext) {
        super(namespace, parentNamespace, rootReducer, reducer, store, config, requestContext);

        if (Telemetry) {
            this.sendActionEvent = Telemetry.sendActionEvent.bind(Telemetry);
            this.sendAppErrorEvent = Telemetry.sendAppErrorEvent.bind(Telemetry);
        }

        if (this.isVerticalBrandingEnabled) {
            defineVerticalHeaderFont();
        }
    }

    /**
     * State mapper.
     * @param state - The current state for the connector
     */
    public mapStateToProps(state: CommonHeaderState): CommonHeaderProps {
        const connectorArgs = {
            handleRenderInfo: this.handleRenderInfo,
            goToMyFeed: this.goToMyFeed,
            goToPersonalize: this.goToPersonalize,
            updateFormParameters: this.updateFormParameters,
            sendTelemetryActionEvent: this.sendActionEvent.bind(this),
            mapContextualNavTelemetryProps: this.mapContextualNavTelemetryProps.bind(this),
            mapHamburgerMenuTelemetryProps: this.mapHamburgerMenuTelemetryProps.bind(this),
            isContextualSecondaryNavEnabled: this.isContextualSecondaryNavEnabled,
            getFormCode: this.getFormCode,
            requestId: this.getRequestId(),
            requestContext: this.requestContext,
            appErrorCallback: this.sendAppErrorEvent,
            languageUpdatedCallback: this.languageUpdatedCallback,
            searchTelemetryContext: this.searchTelemetryContext,
            commonHeaderTelemetryContext: this.commonHeaderTelemetryContext,
            isVerticalBrandingEnabled: this.isVerticalBrandingEnabled
        };

        const inspectorArgs: InspectorArgs<CommonHeaderConfig, CommonHeaderState, CommonHeaderConnectorArgs> = {
            config: this.config,
            children: this.childComponentInstanceList,
            state,
            connectorArgs
        };

        // Call the inspector to get the handled props
        const inspectorResult: InspectorResponse<CommonHeaderProps> = CommonHeaderInspector.inspect(inspectorArgs);

        const props: CommonHeaderProps = {
            ...inspectorResult.props,
            ...this.telemetryTags,
            commonHeaderTelemetryContext: this.commonHeaderTelemetryContext
        };

        return props;
    }

    /**
     * Set the tracking functions (e.g. for using Prime telemetry)
     * @param sendActionEventOverride will override sendActionEvent
     * @param sendAppErrorEventOverride will override sendAppErrorEvent
     * @param sendActionEventByMetadataOverride - function to log action using CustomEvent
     */
    public setTrackingFunctions = (
        sendActionEventOverride: (element?, action?, behavior?, event?, destination?, headline?) => void,
        sendAppErrorEventOverride: (appError) => void) => {
        this.sendActionEvent = sendActionEventOverride;
        this.sendAppErrorEvent = sendAppErrorEventOverride;
    };

    /**
     * Called before component is connected to redux store.
     * @param connectorProps - Connector props
     */
    public async onComponentConnect(connectorProps?: ExperienceInitialProps): Promise<void> {
        // Await the base class because it may be loading in child experiences, and data connectors.
        // NOTE: In the future there will be a SPA page level experience loader that will have a deterministic load order
        // and it will eliminate the need to await on the base class onComponentConnect
        await ComponentConnector.prototype.onComponentConnect.call(this, connectorProps);

        if (connectorProps && connectorProps.context) {
            if (connectorProps.context.overrideRootUrl) {
                this.setOverrideRootUrl(connectorProps.context.overrideRootUrl);
            }

            if (connectorProps.context.isContextualSecondaryNavEnabled) {
                this.setIsContextualSecondaryNavEnabled(connectorProps.context.isContextualSecondaryNavEnabled);
            }

            if (connectorProps.context.verticalKeyOverride) {
                this.setVerticalKeyOverride(connectorProps.context.verticalKeyOverride);
            }

            if (connectorProps.context.primePathOverride) {
                this.setPrimePathOverride(connectorProps.context.primePathOverride);
            }
        }

        this.searchTelemetryContext = this.createSearchTelemetryContext();
        this.commonHeaderTelemetryContext = this.createCommonHeaderTelemetryContext();

        PageBase.getInstance().rootReducer.getDataConnector(ConnectorNamespaces.VerticalPropertyData).then((dataConnector: VerticalPropertyDataConnector) => {
            this.verticalPropertyDataConnector = dataConnector;
        });

        this.verticalKey = getAppEnvironment().HostPage && getAppEnvironment().HostPage.verticalKey;

        if (this.isContextualSecondaryNavEnabled) {
            this.setSelectedPivotWithSecondaryNav(this.primeSecondaryNavigationInfo);
        } else {
            this.setSelectedPivot(this.getRouteByPrimePathOrVerticalKey());
        }

        waitForTtvr().then(() => {
            CommonHeaderActions.updateIsTtvrFired.getActionSender(this).send(true);
        });
    }

    /**
     * Helper to set selected pivot and register observer on router
     * @param routeOverride use this route if provided to select pivot
     */
    private setSelectedPivotWithSecondaryNav(navigationPivots: SecondaryNavigationInfo) {
        if (isNullOrWhiteSpace(navigationPivots.selectedNavMenuId)
            && isNullOrWhiteSpace(navigationPivots.selectedNavItemId)
            && isNullOrWhiteSpace(navigationPivots.selectedSecondaryNavMenuId)
            && isNullOrWhiteSpace(navigationPivots.selectedSecondaryNavItemId)) {
            this.setSelectedPivot(this.getRouteByPrimePathOrVerticalKey());
            return;
        }

        // Get selected pivot from current route
        const currentRoute = peregrineRouter().getCurrentRoute();
        const display = currentRoute && currentRoute.display;
        CommonHeaderActions.updateSelectedTwoLevelNavItems.getActionSender(this).send(
            { id: navigationPivots.selectedNavMenuId, display },
            { id: navigationPivots.selectedNavItemId, display },
            navigationPivots.selectedSecondaryNavMenuId == null ? null : { id: navigationPivots.selectedSecondaryNavMenuId, display },
            navigationPivots.selectedSecondaryNavItemId == null ? null : { id: navigationPivots.selectedSecondaryNavItemId, display }
        );

        // Register observer to update whenever selected pivot is updated
        peregrineRouter().addSwitchListener(this.switchListener);
    }

    /**
     * Helper to set selected pivot and register observer on router
     * @param routeOverride use this route if provided to select pivot
     */
    private setSelectedPivot(pivotIdOverride?: string): void {
        // Get selected pivot from current route
        let currentRoute = peregrineRouter().getCurrentRoute();

        const currentSelectedPivotId = pivotIdOverride || (currentRoute && currentRoute.pivotId);
        if (currentSelectedPivotId) {
            if (!currentRoute) {
                currentRoute = this.getRouteByPivotId(currentSelectedPivotId);
            }

            if (!this.config.disableNavItemSelection) {
                CommonHeaderActions.updateSelectedNavItem.getActionSender(this).send({
                    id: currentSelectedPivotId,
                    display: currentRoute && currentRoute.display
                });
            } else {
                const verticalId = this.getRouteByVerticalKey();
                CommonHeaderActions.updateSelectedTwoLevelNavItems.getActionSender(this).send({
                    id: verticalId,
                    display: currentRoute && currentRoute.display
                }, {
                    id: currentSelectedPivotId,
                    display: currentRoute && currentRoute.display
                }, null, null);

            }
        } else if (this.config.navigationConfig.homeNavigationItem) {
            // If unsuccessful, use the home navigation item as the default
            CommonHeaderActions.updateSelectedNavItem.getActionSender(this).send(this.config.navigationConfig.homeNavigationItem);
        }

        // Register observer to update whenever selected pivot is updated
        peregrineRouter().addSwitchListener(this.switchListener);
    }

    private getRouteByPivotId(pivotId: string): PeregrineRoute {
        const staticRoutes = peregrineRouter().getStaticRoutes();
        if (!staticRoutes) {
            return null;
        }

        return staticRoutes.find(route => route.pivotId === pivotId);
    }

    /**
     * Listener for when route switches so common header can update the selected pivot accordingly
     * @param switchInfo information for when the route switches
     */
    private switchListener = (switchInfo) => {
        if (!switchInfo || !switchInfo.newSelection) {
            return;
        }

        const newSelectedPivotId = switchInfo.newSelection.pivotId;
        if (this.getCurrentState().selectedNavItem && this.getCurrentState().selectedNavItem.id !== newSelectedPivotId) {
            CommonHeaderActions.updateSelectedNavItem.getActionSender(this).send({
                id: newSelectedPivotId,
                display: switchInfo.newSelection.display
            });
        }
    };

    /**
     * Handler for rendering content based on the selected navigation item
     * @param id navigation item id
     * @param renderInfo information needed to render content
     * @param telemetryInfo telemetry info
     */
    private handleRenderInfo = (event: CustomEvent): void => {
        const navItem: ContextualNavItem | HamburgerMenuItem = event.detail.selectedNav;

        if (!navItem) {
            this.sendAppErrorEvent({
                ...CommonHeaderNavigationFailed,
                message: "Failed to navigate to new page, no nav item available",
                pb: {
                    ...CommonHeaderNavigationFailed.pb,
                    customMessage: `Config id: ${this.configHref}`
                }
            });
            return;
        }

        const { id, renderInfo, verticalKey } = navItem;
        if (!id || !renderInfo) {
            this.sendAppErrorEvent({
                ...CommonHeaderNavigationFailed,
                message: "Failed to navigate to new page, nav item is missing id or renderInfo",
                pb: {
                    ...CommonHeaderNavigationFailed.pb,
                    customMessage: `Config id: ${this.configHref}`
                }
            });
            return;
        }

        const shouldForceClientSideRouting = this.config.navigationConfig.shouldForceRouterForNews
            && verticalKey === "news"
            && this.verticalKey === "news"
            && appEnvironment.AppType === "hub";

        if (shouldForceClientSideRouting) {
            renderInfo.renderType = RenderType.Router;
        }
        // Prime hybrid navigating back to Peregrine
        if (renderInfo.renderType === RenderType.Router && this.overrideRootUrl && !shouldForceClientSideRouting) {
            // this.navigateWithOverrideUrl already puts a prefix on the URL, so usePrefix is set to false here
            const path = renderInfo.usePathNameAsIs ? getRoutePath(renderInfo.path, null, false) : getRoutePath(`interest/${renderInfo.path}`, null, false);
            this.navigateWithOverrideUrl(path, event, renderInfo.context && renderInfo.context.excludeFeedPrefix);
            return;
        }

        // change externalUrl according to current location
        const state = this.getCurrentState();
        if (state && id !== state.selectedNavItem?.id) {
            const relatedLinks = (state["SharedState"] as ExperiencesSharedStateState)?.location?.relatedLinks;
            if (relatedLinks && relatedLinks[id]) {
                renderInfo.externalUrl = relatedLinks[id];
            }
        }

        switch (renderInfo.renderType) {
            case RenderType.Router: {
                const url = renderInfo.usePathNameAsIs ? renderInfo.path : `interest/${renderInfo.path}`;

                peregrineRouter().pushHistory(url, "", renderInfo.context);
                this.trackNavItemClick(event, url);

                // We only need to update state for this case because the other cases navigate away from the SPA
                CommonHeaderActions.updateSelectedNavItem.getActionSender(this).send(navItem);
                return;
            }

            case RenderType.ExternalLink:
                if (!UrlUtility.isValidUrl(renderInfo.externalUrl, true)) {
                    this.sendAppErrorEvent({
                        ...CommonHeaderNavigationFailed,
                        message: "Failed to navigate to new page, tried to navigate to item but invalid url",
                        pb: {
                            ...CommonHeaderNavigationFailed.pb,
                            customMessage: `Config id: ${this.configHref}; Nav item id: ${id}; Nav item url: ${renderInfo.externalUrl};`
                        }
                    });
                    return;
                }

                this.trackNavItemClick(event, renderInfo.externalUrl);
                if (renderInfo.openInNewTab == true) {
                    window.open(renderInfo.externalUrl, "_blank");
                } else {
                    window.location.href = renderInfo.externalUrl;
                }

                return;

            case RenderType.PrimeExternalLink: {
                // We manually append the OCID because the telemetry click listener that normally appends the OCIDis bypassed when
                // we use sendActionEventByMetadata. If we aren't forwarding the OCID. then set the OCID
                const url = this.getUrlWithOcid(this.formatUrlDomain(renderInfo.externalUrl), !this.config.forwardOcidOnPrimeExternal);
                if (!url) {
                    this.sendAppErrorEvent({
                        ...CommonHeaderNavigationFailed,
                        message: "Failed to navigate to new page, tried to navigate to item but invalid url",
                        pb: {
                            ...CommonHeaderNavigationFailed.pb,
                            customMessage: `Config id: ${this.configHref}; Nav item id: ${id}; Nav item url: ${renderInfo.externalUrl};`
                        }
                    });
                    return;
                }

                this.trackNavItemClick(event, url);
                if (renderInfo.openInNewTab == true) {
                    window.open(url, "_blank");
                } else {
                    window.location.href = url;
                }
                return;
            }

            default:
                this.sendAppErrorEvent({
                    ...CommonHeaderNavigationFailed,
                    message: "Failed to navigate to new page, tried to navigate to item but missing renderType",
                    pb: {
                        ...CommonHeaderNavigationFailed.pb,
                        customMessage: `Config id: ${this.configHref}; Nav item id: ${id};`
                    }
                });
        }
    };

    /**
     * Get pivot to select based on primePath and verticalKey. Return undefined if none found.
     */
    private getRouteByPrimePathOrVerticalKey(): string {
        if (this.primePathOverride && this.config.primePathToNavItemIdMap[this.primePathOverride] !== undefined) {
            return this.config.primePathToNavItemIdMap[this.primePathOverride];
        }

        if (this.primePathOverride) {
            let indexOfPathComponent = this.primePathOverride.lastIndexOf("/");
            let primePath = this.primePathOverride;
            while (indexOfPathComponent != -1) {
                primePath = primePath.substring(0, indexOfPathComponent);
                if (primePath && this.config.primePathToNavItemIdMap[primePath] !== undefined) {
                    return this.config.primePathToNavItemIdMap[primePath];
                }

                indexOfPathComponent = primePath.lastIndexOf("/");
            }
        }

        // If there is a vertical key override, first try to get the vertical key from the headdata. If not available, then use the passed in value
        return this.getRouteByVerticalKey();
    }

    private getRouteByVerticalKey(): string {
        const headDataClientSettings = headData.ClientSettings;
        return (headDataClientSettings && this.config.verticalToNavItemIdMap[headDataClientSettings.verticalKey]) || (this.verticalKeyOverride && this.config.verticalToNavItemIdMap[this.verticalKeyOverride]);
    }

    /**
     * Logs telemetry for navigation event
     * @param event - the custom event from web component
     * @param url - the url to navigate to
     */
    private trackNavItemClick(event: CustomEvent, url: string): void {
        if (Telemetry) {
            return;
        }

        const navItem = event.detail.selectedNav;
        // Log using Prime tracking function
        this.sendActionEvent(null, null, null, { type: "click", target: event.target }, url, navItem.display);
    }

    /**
     * Given a url, return a new url with the ocid query param and other query params. If there is no ocid, return the given url.
     * @param url url string to add current ocid query param to
     * @param forceAppendOcid set to true if you want to explicitly set the ocid, regardless of the current ocid
     * @param useBlockedList set to true to clean search params of the blocked query params
     */
    private getUrlWithOcid(url: string, forceAppendOcid = false): string {
        const currentUrlSearchParams = new URLSearchParams(window.location.search);

        // remove blocked query params from URL
        blockedQueryParams.forEach(key => {
            currentUrlSearchParams.delete(key);
        });

        const formattedUrl = this.tryGetUrl(url);
        if (!formattedUrl) {
            return;
        }

        if (forceAppendOcid) {
            formattedUrl.searchParams.set("ocid", Telemetry.getPageMetadata().contract.page.ocid);
            currentUrlSearchParams.forEach((value, key) => {
                if (key.toLowerCase() !== "ocid") {
                    formattedUrl.search = formattedUrl.search.concat(`&${key}=${value}`);
                }
            });
            return formattedUrl.toString();
        }

        const ocid = currentUrlSearchParams.get("ocid");
        if (!ocid) {
            if (this.verticalKeyOverride == "sports") {
                // Needed as sports need item forwarded for super app scenario
                currentUrlSearchParams.forEach((value, key) => formattedUrl.search = formattedUrl.search.concat(`&${key}=${value}`));
                return formattedUrl.toString();
            }

            return url;
        }

        formattedUrl.searchParams.set("ocid", ocid);
        currentUrlSearchParams.forEach((value, key) => {
            if (key.toLowerCase() !== "ocid") {
                formattedUrl.search = formattedUrl.search.concat(`&${key}=${value}`);
            }
        });
        return formattedUrl.toString();
    }

    /**
     * Updates url to maintain current domain
     * @param url url string to format
     */
    private formatUrlDomain(urlString: string): string {
        const url = this.tryGetUrl(urlString);
        if (!url) {
            return;
        }
        // a.msn.com is short URL redirection service
        // so don't replace it
        if (url.hostname !== "a.msn.com") {
            const currentHostname = window.location.hostname;
            url.hostname = currentHostname;
        }

        return url.toString();
    }

    /**
     * Method to navigate to "My Feed"
     */
    private goToMyFeed = () => {
        this.navigateToStaticPath(RouteNames.MyFeed);
    };

    /**
     * Method to navigate to "Personalize"
     */
    private goToPersonalize = () => {
        this.navigateToStaticPath(RouteNames.Personalize);
    };

    /**
     * Helper for navigating to a static path, e.g. My Feed or Personalize
     * @param event custom event
     * @param route route name
     */
    private navigateToStaticPath(route: RouteNames): void {
        if (this.overrideRootUrl) {
            this.navigateWithOverrideUrl(CanonicalNamePathMap[route]);
            return;
        }
        peregrineRouter().pushHistory(route);
    }

    /**
     * Navigates to the override url appended with the passed in path
     * @param path path to append to override url
     * @param event custom event
     */
    private navigateWithOverrideUrl(path: string, event?: CustomEvent, excludeFeedPrefix = false): void {
        const destinationUrl = this.getUrlFromPath(path, excludeFeedPrefix);
        if (!destinationUrl) {
            this.sendAppErrorEvent({
                ...CommonHeaderNavigationFailed,
                message: "Failed to navigate to new page, tried to navigate to item but invalid url was provided",
                pb: {
                    ...CommonHeaderNavigationFailed.pb,
                    customMessage: `Config id: ${this.configHref}; Path: ${path};`
                }
            });
            return;
        }

        if (event) {
            this.trackNavItemClick(event, destinationUrl);
        }

        // Forward ocid if available when navigating from Prime back to Peregrine
        window.location.href = destinationUrl;
    }

    /**
     * Gets url with ocid and correct domain based on path provided
     * @param path - path to use for url
    */
    private getUrlFromPath(path: string, excludeFeedPrefix = false): string | undefined {
        const urlString = this.config && this.config.navigationConfig && this.config.navigationConfig.enableLocaleFeedPrefix ?
            "https://www.msn.com" + `/${appEnvironment.CurrentMarket}${excludeFeedPrefix ? "" : "/feed"}${path}` :
            this.overrideRootUrl + path;
        const overrideUrl = this.tryGetUrl(urlString);
        if (!overrideUrl) {
            return;
        }

        if (this.config.includePCSOnlyFlag) {
            overrideUrl.searchParams.set("pcsonly", "true");
        }

        return this.getUrlWithOcid(this.formatUrlDomain(overrideUrl.toString()), false);
    }

    /**
     * Helper to try and create url object. Will check if string is a valid url and throw app error if not.
     * @param url url string
     * @returns url object from string
     */
    private tryGetUrl(url: string): URL {
        if (!url || !UrlUtility.isValidUrl(url, true)) {
            this.sendAppErrorEvent({
                ...CommonHeaderInvalidUrl,
                message: "Tried to create an invalid url",
                pb: {
                    ...CommonHeaderInvalidUrl.pb,
                    customMessage: `Config id: ${this.configHref}; Invalid url: ${url};`
                }
            });
            return;
        }

        return new URL(url);
    }

    /**
     * Get form code from form parameters and handles vertical and cobrand form code
     * @param formParameters Form parameters
     */
    private getFormCode = (formParameters: Array<SearchFormParameters>): string => {
        if (!formParameters || !formParameters.length) {
            this.sendAppErrorEvent({
                ...CommonHeaderMissingFormCode,
                message: "Form parameters are not configured, searchConfig is missing formParameters",
                pb: {
                    ...CommonHeaderMissingFormCode.pb,
                    customMessage: `Config id: ${this.configHref}`
                }
            });

            return "";
        }

        // Try to set the relevant form codes
        this.trySetFormCodeForVertical(formParameters, FormCodeInputName);
        this.trySetCoBrandFormCode(formParameters, FormCodeInputName);
        const formCodeItem = formParameters.find(formParameter => formParameter.name === FormCodeInputName);
        const formCode = formCodeItem ? formCodeItem.value : "";

        if (!formCode || formCode === "") {
            this.sendAppErrorEvent({
                ...CommonHeaderMissingFormCode,
                message: "Form code look up based on 'form' keyword not found in form parameters config",
                pb: {
                    ...CommonHeaderMissingFormCode.pb,
                    customMessage: `Config id: ${this.configHref}`
                }
            });
        }

        return formCode;
    };

    /**
     * Try set form code for verticals
     * @param formParameters Form parameters
     * @param input name e.g. "form"
     */
    private trySetFormCodeForVertical(formParameters: Array<SearchFormParameters>, inputName: string): void {
        if (!this.verticalPropertyDataConnector || !this.verticalKey) {
            return;
        }

        const formCode: string = this.verticalPropertyDataConnector.getSearchFormCodeForCurrentVertical(this.verticalKey);
        if (!this.populateFormParameter(formParameters, inputName, formCode)) {
            this.sendAppErrorEvent({
                ...CommonHeaderMissingFormCode,
                message: "Form code look up based on 'form/searchFormCode' keyword not found in form parameters config, failed to set form code for vertical",
                pb: {
                    ...CommonHeaderMissingFormCode.pb,
                    customMessage: `Config id: ${this.configHref}; vertical key: ${this.verticalKey};`
                }
            });
        }
    }

    /**
     * Set form code in form parameters for cobrand only when cobrand exists in qsp
     * @param formParameters Form parameters
     * @param input name e.g. "form"
     */
    private trySetCoBrandFormCode(formParameters: Array<SearchFormParameters>, inputName: string): void {
        const configFormCodes = this.config.searchBoxConfig.formCodes;
        const locationHref = getContextData(this.requestContext.data.locationHref, "locationHref");
        const coBrandParams = UrlUtility.getQueryParameterByName("cobrand", locationHref);
        if (!configFormCodes || !coBrandParams || !coBrandParams.length) {
            return;
        }

        if (!this.populateFormParameter(formParameters, inputName, configFormCodes.coBrand)) {
            this.sendAppErrorEvent({
                ...CommonHeaderMissingFormCode,
                message: "Form code look up based on 'form/searchFormCode' keyword not found in form parameters config, failed to set form code for cobrand",
                pb: {
                    ...CommonHeaderMissingFormCode.pb,
                    customMessage: `Config id: ${this.configHref}`
                }
            });
        }
    }

    /**
     * Set form code in form parameters
     * @param formParameters Form parameters
     * @param inputName name e.g. "form"
     * @param inputValue value e.g. "formCode"
     */
    private populateFormParameter(formParameters: Array<SearchFormParameters>, inputName: string, inputValue: string): boolean {
        const formParameterIndex: any = formParameters.findIndex(p => p.name === inputName);
        if (formParameterIndex === -1 || !inputValue) {
            return false;
        }

        formParameters[formParameterIndex].value = inputValue;
        return true;
    }

    private createSearchTelemetryContext(): SearchTelemetryContext {
        if (!this.telemetryObject) {
            return null;
        }

        const contract = {
            content: {
                headline: "",
                type: TelemetryContentType.TextSearch
            },
            behavior: TelemetryBehaviorType.TextSearch,
            type: TelemetryType.SearchBox
        };

        const formObject = this.telemetryObject.addOrUpdateChild({
            ...contract,
            name: CommonHeaderTelemetry.searchName
        });

        const inputObject = formObject.addOrUpdateChild({
            ...contract,
            name: CommonHeaderTelemetry.searchWebName,
            action: TelemetryActionType.Submit
        });

        const buttonObject = formObject.addOrUpdateChild({
            name: CommonHeaderTelemetry.searchInputName,
            behavior: TelemetryBehaviorType.Undefined
        });

        return {
            formObject,
            inputObject,
            buttonObject,
            formTag: formObject.render(),
            inputTag: inputObject.getMetadataTag(),
            buttonMetaTag: buttonObject.getMetadataTag()
        };
    }

    /**
     * Update the form parameters
     */
    private updateFormParameters = (): Array<SearchFormParameters> => {
        if (!this.config.searchBoxConfig || !this.config.searchBoxConfig.formParameters) {
            return [];
        }

        const updatedFormParameters = cloneDeep(this.config.searchBoxConfig.formParameters);
        const requestId = this.getRequestId();

        // "refig" parameter is passed to bing.com/search page as a query parameter to correlate search with MSN page request
        this.updateFormParameter(updatedFormParameters, "refig", requestId);
        this.updateFormParameter(updatedFormParameters, "mkt", getAppEnvironment().CurrentMarket);

        let locationHref = "";
        if (this.requestContext && this.requestContext.data && this.requestContext.data.locationHref) {
            locationHref = getContextData(this.requestContext.data.locationHref, "locationHref");
        }
        const ocid = UrlUtility.getQueryParameterByName("ocid", locationHref);
        if (ocid && !PageUtility.isDhpPage() && !PageUtility.isNtpPage()) {
            updatedFormParameters.push({ value: ocid, name: "ocid" });
        }

        return updatedFormParameters;
    };

    /**
     * Update the search form input parameter
     * @param formParameters Form parameters
     * @param name Form parameter name
     * @param value Form parameter value
     */
    private updateFormParameter(formParameters: SearchFormParameters[], name: string, value: string): void {
        if (!formParameters || !value) {
            return;
        }

        const parameterIndex = formParameters.findIndex(p => p.name === name);
        parameterIndex !== -1 ?
            formParameters[parameterIndex].value = value :
            formParameters.push({ name: name, value: value });
    }

    /**
     * Gets the request id
     */
    private getRequestId(): string {
        if (Telemetry) {
            const requestIdFromTelemetry = Telemetry.getRequestId();

            if (requestIdFromTelemetry) {
                return requestIdFromTelemetry;
            }
        }

        return getAppEnvironment().ActivityId.toLowerCase();
    }

    /**
     * Callback when the market has been updated
     * @param market New market to switch to
     */
    private languageUpdatedCallback = (market: string): void => {
        pdpServiceClient.updateMarketPreference(market).then(() => {
            WindowsPageSettingsActions.marketUpdated.getGlobalActionSender().send(market);
        });
    };

    private mapNavigationItemTelemetryProps(telemetryObject: TelemetryObjectBase, navItem: NavigationItem, prefix?: string): TelemetryMetadataAttribute {
        const { renderInfo } = navItem;
        const { renderType, usePathNameAsIs, path, externalUrl } = renderInfo;

        let destinationUrl = "";
        if (renderType === RenderType.Router && path) {
            const destinationUrlPath = usePathNameAsIs ? CanonicalNamePathMap[path] || `/${path}` : `/interest/${path}`;
            destinationUrl = this.getUrlFromPath(destinationUrlPath, navItem.renderInfo && navItem.renderInfo.context && navItem.renderInfo.context.excludeFeedPrefix);
        }
        if (renderType !== RenderType.Router && externalUrl) {
            destinationUrl = this.getUrlWithOcid(externalUrl);
        }

        let name = navItem.display;
        if (this.config.localizedStrings && navItem.displayKey) {
            name = this.config.localizedStrings && this.config.localizedStrings[navItem.displayKey];
        }

        return telemetryObject.addOrUpdateChild({
            name,
            action: TelemetryActionType.Click,
            behavior: TelemetryBehaviorType.Navigate,
            content: {
                headline: name
            },
            destinationUrl,
            overrideDestinationUrl: destinationUrl,
            feed: {
                id: navItem.id,
                name,
                type: "category filter"
            }
        }).getMetadata();
    }

    private mapContextualNavTelemetryProps(navItem: NavigationItem): TelemetryMetadataAttribute {
        if (!this.telemetryObject) {
            return;
        }

        return this.mapNavigationItemTelemetryProps(
            this.contextualNavTelemetry || this.telemetryObject.addOrUpdateChild({
                name: "PivotsNav",
                type: TelemetryType.Module
            }),
            navItem,
            "nav-item"
        );
    }

    private mapHamburgerMenuTelemetryProps(navItem: NavigationItem): TelemetryMetadataAttribute {
        if (!this.telemetryObject) {
            return;
        }

        return this.mapNavigationItemTelemetryProps(
            this.hamburgerMenuTelemetry || this.telemetryObject.addOrUpdateChild({
                name: "Hamburger",
                type: TelemetryType.ActionButton,
                behavior: TelemetryBehaviorType.Show,
                content: {
                    type: TelemetryContentType.Feed,
                    headline: "Hamburger"
                }
            }),
            navItem,
            "menu-item"
        );
    }

    /**
     * Creates the common header telemetry context, including the telemetry tags for the different modules
     * within the common header
     * @param parentTelemetryObject parent telemetry
     */
    private createCommonHeaderTelemetryContext(): CommonHeaderTelemetryContext {
        if (!this.telemetryObject) {
            return null;
        }

        if (!this.hamburgerMenuTelemetry) {
            this.hamburgerMenuTelemetry = this.telemetryObject.addOrUpdateChild({
                name: "Hamburger",
                type: TelemetryType.ActionButton,
                behavior: TelemetryBehaviorType.Show,
                content: {
                    type: TelemetryContentType.Feed,
                    headline: "Hamburger"
                }
            });
        }

        if (!this.contextualNavTelemetry) {
            this.contextualNavTelemetry = this.telemetryObject.addOrUpdateChild({
                name: "PivotsNav",
                type: TelemetryType.Module
            });
        }

        const closeHamburgerMetaTag = this.hamburgerMenuTelemetry.addOrUpdateChild({
            name: "CloseHamburger",
            action: TelemetryActionType.Click,
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.Close,
            content: {
                type: TelemetryContentType.Feed
            }
        }).getMetadataTag();

        const overflowMetaTag = this.contextualNavTelemetry.addOrUpdateChild({
            name: "TopNavEllipsis",
            action: TelemetryActionType.Click,
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.More,
            content: {
                type: TelemetryContentType.Feed
            }
        }).getMetadataTag();

        const personalizeButtonMetaTag = this.contextualNavTelemetry.addOrUpdateChild({
            name: "Personalize",
            action: TelemetryActionType.Click,
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.Navigate,
            content: {
                type: TelemetryContentType.Feed
            }
        }).getMetadataTag();

        const logoTag = this.telemetryObject.addOrUpdateChild({ name: "MicrosoftLogo" }).render();
        const meControlTag = this.telemetryObject.addOrUpdateChild({ name: "MeControl", type: TelemetryType.ActionButton }).render();
        const settingsTelemetry = this.telemetryObject.addOrUpdateChild({
            name: "Settings",
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.Open,
            content: {
                type: TelemetryContentType.Settings
            }
        });
        const settingsMetaTag = settingsTelemetry.getMetadataTag();
        const settingsPersonalizeButtonMetaTag = settingsTelemetry.addOrUpdateChild({
            name: "SettingsPersonalizeButton",
            type: TelemetryType.ActionButton,
            content: {
                type: TelemetryContentType.Settings
            }
        }).getMetadataTag();

        const settingsCloseButtonMetaTag = settingsTelemetry.addOrUpdateChild({
            name: "Close",
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.Close,
            content: {
                type: TelemetryContentType.Settings
            }
        }).getMetadataTag();

        const settingsLanguageSelectorTelemetry = settingsTelemetry.addOrUpdateChild({
            name: "MarketSelector",
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.Customize,
            content: {
                type: TelemetryContentType.Settings,
                headline: "MarketSelectorOptions"
            }
        });
        const settingsLanguageSelectorMetaTag = settingsLanguageSelectorTelemetry.getMetadataTag();
        const languageOptions = windowsMarkets.sort(compareDisplayNameDesc).map(market => ({
            ...market,
            marketSelectorOptionMetaTag: settingsLanguageSelectorTelemetry.addOrUpdateChild({
                name: "MarketSelector",
                behavior: TelemetryBehaviorType.Customize,
                type: TelemetryType.ActionButton,
                content: {
                    type: TelemetryContentType.Settings,
                    headline: market.displayString
                }
            }).getMetadataTag()
        }));

        const interestsNavOptionsMetadata = this.hamburgerMenuTelemetry.addOrUpdateChild({
            name: "InterestsNavOptions",
            action: TelemetryActionType.Click,
            behavior: TelemetryBehaviorType.Show,
            content: {
                type: TelemetryContentType.Feed,
                headline: "Interests Nav Options"
            }
        });
        const hamburgerMenuExpandedChangeTelemetryCallback = (event: CustomEvent) => {
            const hamburgerMenuItem = event.detail;
            if (!hamburgerMenuItem) {
                return;
            }

            if (Telemetry) {
                TelemetryDOM.setAttribute(hamburgerMenuItem as HTMLElement, null, interestsNavOptionsMetadata);
                Telemetry.sendActionEvent(hamburgerMenuItem, TelemetryActionType.Click, TelemetryBehaviorType.Show);
                return;
            }

            // Log using Prime tracking function
            this.sendActionEvent(null, null, null, { type: "click", target: hamburgerMenuItem }, null, "Interests Nav Options");
        };

        return {
            hamburgerMenuTelemetry: this.hamburgerMenuTelemetry,
            contextualNavTelemetry: this.contextualNavTelemetry,
            overflowMetaTag,
            closeHamburgerMetaTag,
            logoTag,
            meControlTag,
            settingsMetaTag,
            settingsPersonalizeButtonMetaTag,
            settingsCloseButtonMetaTag,
            settingsLanguageSelectorMetaTag,
            hamburgerMenuExpandedChangeTelemetryCallback,
            languageOptions,
            personalizeButtonMetaTag
        };
    }
}
